import { useSearchParams } from "react-router";
import { Card } from "../components/Card";
import { Cta } from "../components/Cta";
import { Section } from "../components/Section";
import { StatValue } from "../components/StatValue";
import {
  PER_MODEL_CREDITS,
  PLANS,
  purchasingAvailable,
  TIER_LABEL,
  TOP_UP,
  topUpAvailable,
} from "../config/plans";
import { useAuth } from "../features/auth/context";
import { UpgradeDialog } from "../features/billing/UpgradeDialog";
import { useCheckout } from "../features/billing/useCheckout";
import { useBillingPortal } from "../features/billing/useBillingPortal";
import { useUpgrade } from "../features/billing/useUpgrade";
import { useCatalogue, useEntitlements } from "../features/catalogue/queries";
import { isReachable, priceToday } from "../config/catalogue";
import { safeNext } from "../lib/safeNext";
import { formatResetDate } from "../lib/formatResetDate";
import { usePageMeta } from "../lib/usePageMeta";
import styles from "./Pricing.module.css";

/**
 * Pricing.
 *
 * The commercial risk this page carries: Premium and Pro mean "all models", and
 * today that is ONE model, in beta. Selling "all models" against that is a
 * misleading-claims problem under the ACL — the exposure kev-marketing-plan.md
 * already flags.
 *
 * The mitigation is structural rather than editorial: every count and every list
 * below is DERIVED from the catalogue. The page physically cannot claim a model
 * that isn't built, and the day one ships the copy updates itself.
 */
export function Pricing() {
  usePageMeta("pricing");
  const [params] = useSearchParams();
  const { status } = useAuth();
  const catalogue = useCatalogue();
  const entitlements = useEntitlements();
  const { start, starting, error } = useCheckout();
  const up = useUpgrade();
  /* Plan CHANGES and cancellation both land on Stripe's own pages — see the
     branches below for why the confirmation belongs there rather than here. */
  const portal = useBillingPortal();

  /**
   * Move an existing subscriber to another tier, in either direction.
   *
   * STRIPE FIRST, OUR DIALOG SECOND, and the fallback is not belt-and-braces —
   * it is the difference between shipping this today and waiting on a dashboard
   * setting. Stripe's confirm-the-difference page requires `subscription_update`
   * enabled on the portal configuration, per mode; until it is, the update flow
   * is refused and a customer pressing Upgrade would simply be stuck. The in-app
   * preview still works, still states the same proration off Stripe's own
   * invoice preview, and still swaps the price on the subscription they have.
   *
   * So: attempt the Stripe hand-off, and if it is not available, open our own
   * dialog rather than showing an error to someone trying to give us money. The
   * error copy is only reached if BOTH are unavailable, which is why
   * `reportFailure` is separate from `open`.
   */
  /* Typed off PLANS rather than TierId: TierId includes "free", which is not a
     thing anyone can be moved TO, and `up.preview` rightly refuses it. Taking
     the element type keeps this in step with the list being rendered. */
  async function changePlan(plan: (typeof PLANS)[number]) {
    const wentToStripe = await portal.open(
      { intent: "update", priceId: plan.priceId, grants: { kind: "tier", tier: plan.id } },
      // Quiet, because there is somewhere else to go. See below.
      { quiet: true },
    );
    if (wentToStripe) return;
    try {
      await up.preview({ priceId: plan.priceId, tier: plan.id });
    } catch {
      portal.reportFailure();
    }
  }

  const models = catalogue.data?.models ?? [];
  /* Has the SERVER answered, or is this still the compiled-in seed?
   *
   * `useCatalogue` supplies `config/catalogue.ts` as `initialData` with
   * `initialDataUpdatedAt: 0`, so `catalogue.data` is populated on the very first
   * frame and `dataUpdatedAt` stays 0 until the revalidation lands. Every
   * DISPLAY use of that seed is fine and is the whole point — the marketing copy
   * paints with no loading flash.
   *
   * A `priceId` is not a display value. The seed carries the LIVE ids in every
   * build including dev's, and the server answers with the ids for its own
   * environment (RIPK `api/kev_prices.py`), so buying from the seed in dev sends
   * a live id to a test-mode key: `resource_missing`, at the till. The comment a
   * few lines below already names this hazard in the other direction — "a live
   * one sends a customer to a checkout backed by a test-mode price" — and until
   * the two id sets diverged on 2026-08-27 they were the same ids, so it could
   * not happen. Now it can, and only during the first round-trip, which is
   * exactly the kind of window that reproduces for nobody.
   *
   * So: paint from the seed, buy only from the server. */
  const priced = catalogue.dataUpdatedAt > 0;
  const reachable = models.filter(isReachable);
  const coming = models.filter((m) => m.build === "soon");
  const purchasable = models.filter((m) => m.purchasable);

  /* Preselected when arriving from a locked tile or a deep link. */
  const focusId = params.get("model");
  const next = safeNext(params.get("next"), "/app");
  const tier = entitlements.data?.tier ?? "free";
  const ownedSubs = entitlements.data?.modelSubs ?? [];
  const subscribed = tier !== "free" || ownedSubs.length > 0;

  /*
   * Mirrors PLAN_RANK in handler_billing.py, and has to: the server refuses a
   * downgrade with a 409, so offering the button would just be a slower way to
   * show an error. A per-model subscription is rank 1 whatever the model, since
   * either tier grants every model.
   *
   * This is the SPA copy and the server's is the one that counts — same
   * arrangement as canAccess and require_model_access.
   */
  const RANK: Record<string, number> = { premium: 2, pro: 3 };
  const currentRank = tier !== "free" ? RANK[tier] : ownedSubs.length > 0 ? 1 : 0;

  /*
   * Don't offer to sell anything until we know what they own.
   *
   * `tier` falls back to "free" while the query is in flight, so a subscriber
   * arriving here gets Subscribe buttons that flip to "Your plan" a beat later.
   * It is the nav's boot-window argument with money attached: the worst moment
   * to imply someone isn't a customer is the moment they are one, and a click
   * landing in that window starts a checkout for something they already hold.
   */
  const knowsEntitlement = status !== "authenticated" || !entitlements.isLoading;

  const renewsOn = formatResetDate(entitlements.data?.credits.resetAt);
  /* TIER_LABEL, not a capitalised wire id. `tier[0].toUpperCase() + slice(1)`
     was a third copy of the tier-name rule living next to two others, and it
     was the copy that could not survive the rename: it derives the label FROM
     the identifier, so `free` could only ever come out as "Free". A tier on the
     `free` id with model subs still reads as its model count here, which is the
     more useful answer to "what am I on" — the label is what the OTHER branch
     needed fixing for. */
  const planLabel =
    tier !== "free"
      ? TIER_LABEL[tier]
      : `${ownedSubs.length} model${ownedSubs.length === 1 ? "" : "s"}`;

  return (
    <Section
      eyebrow="Pricing"
      heading={subscribed ? "Your plan" : "Pay for what you use"}
      tone="ink"
      level={1}
      heroScroll
    >
      {/* Someone already paying has landed here by mis-click or to change
          something — not to be sold to. Say where they stand and give them the
          way back into the product. The ladder below stays: "what else could I
          move to" is the other honest reason to be on this page. */}
      {subscribed ? (
        <div className={styles.standing}>
          <p className={styles.lede}>
            You&rsquo;re on <StatValue tone="data">{planLabel}</StatValue>
            {tier !== "free" && (
              <>
                {" "}
                — every model that answers questions today, plus whatever lands while
                you&rsquo;re subscribed
              </>
            )}
            {renewsOn && <> · renews {renewsOn}</>}.
          </p>
          <Cta to="/app/models">Your models</Cta>
        </div>
      ) : (
        <p className={styles.lede}>
          {purchasable.length > 0
            ? "Subscribe to a single model, or take the lot."
            : "Every model, on either plan."}{" "}
          Every plan includes a monthly credit allowance, and we show you what a question
          costs before we run it.
        </p>
      )}

      {/* Derived. If this ever reads oddly, the catalogue changed and the claim
          followed it — which is the point. */}
      <p className={styles.reality}>
        {reachable.every((m) => m.build === "beta") ? "Live now in beta" : "Live now"}:{" "}
        <StatValue tone="data">{countWord(reachable.length)}</StatValue> model
        {reachable.length === 1 ? "" : "s"}.
        {coming.length > 0 && (
          <>
            {" "}
            {listNames(coming.map((m) => m.name))} {coming.length === 1 ? "is" : "are"} next.
          </>
        )}
      </p>

      {/* Hidden while no model is sold on its own (2026-10-06: Premium and Pro
          only). Comes back by itself when catalogue.json marks one purchasable. */}
      {purchasable.length > 0 && (
        <>
        <h2 className={styles.credits} style={{ marginBottom: 12 }}>
          Single model
        </h2>
        <div className={styles.grid}>
          {purchasable.map((m) => {
            const owned = ownedSubs.includes(m.id) || tier !== "free";
            return (
              <Card key={m.id} className={`${styles.card} ${focusId === m.id ? styles.selected : ""}`}>
                <span className={styles.planName}>{m.name}</span>
                {(() => {
                  const p = priceToday(m);
                  if (!p) return null;
                  return (
                    <>
                      <span className={styles.price}>
                        ${p.now.toFixed(2)}
                        <span className={styles.unit}> /month</span>
                      </span>
                      {/* One figure since 2026-08-31: quoted monthly, charged
                          monthly. The "billed monthly at …" line that used to sit
                          here carried the amount that actually left the account,
                          and is gone because it would now repeat the headline.
                          The note remains — Terms §6 still requires naming what
                          someone joining after the promotion will pay. */}
                      {p.note && <span className={styles.priceNote}>{p.note}</span>}
                    </>
                  );
                })()}
                <span className={styles.credits}>{PER_MODEL_CREDITS} credits a month</span>
                <p className={styles.blurb}>{m.blurb}</p>
                {m.sampleNote && <p className={styles.included}>{m.sampleNote}</p>}
                <div className={styles.action}>
                  {!knowsEntitlement ? (
                    <span className={styles.current}>Checking…</span>
                  ) : owned ? (
                    <span className={styles.current}>
                      {tier !== "free" ? `Included with ${TIER_LABEL[tier]}` : "Subscribed"}
                    </span>
                  ) : !purchasingAvailable() ? (
                    /* ABOVE the sign-in branch, deliberately. The model is real and
                       the price is real; it just cannot be bought yet. Sending
                       someone to /login first would spend their sign-in on a dead
                       end — they arrive back here and still cannot buy. Saying so
                       up front is the whole of it: a disabled Subscribe button
                       reads as something broken, and a live one sends a customer to
                       a checkout backed by a test-mode price. */
                    <span className={styles.soon}>Coming soon</span>
                  ) : status !== "authenticated" ? (
                    <Cta to={`/login?next=${encodeURIComponent(`/pricing?model=${m.id}`)}`}>
                      Sign in to subscribe
                    </Cta>
                  ) : (
                    <Cta
                      disabled={starting || !priced || !m.priceId}
                      onClick={() => void start({ kind: "model", modelId: m.id }, m.priceId!, next)}
                    >
                      {!priced ? "Checking price…" : starting ? "Starting…" : "Subscribe"}
                    </Cta>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
        </>
      )}

      <h2 className={styles.credits} style={{ marginBottom: 12 }}>
        Everything
      </h2>
      <div className={styles.grid}>
        {PLANS.map((plan) => {
          const current = tier === plan.id;
          return (
            <Card key={plan.id} className={styles.card}>
              <span className={styles.planName}>{plan.name}</span>
              <span className={styles.price}>
                ${plan.monthlyPriceAud.toFixed(2)}
                <span className={styles.unit}> /month</span>
              </span>
              <span className={styles.credits}>
                {plan.monthlyCredits.toLocaleString()} credits a month
              </span>
              <p className={styles.blurb}>{plan.blurb}</p>
              <ul className={styles.perks}>
                {plan.perks.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
              {/* "Included today: …" came off the plan cards on 2026-10-05
                  (Ned's site cut list): the banner above already names the
                  live count. What a bundle adds later stays, derived from the
                  catalogue. Never hardcode this list. */}
              {coming.length > 0 && (
                <p className={styles.included}>
                  <b>When they land:</b> {coming.map((m) => m.name).join(", ")}
                </p>
              )}
              <div className={styles.action}>
                {!knowsEntitlement ? (
                  <span className={styles.current}>Checking…</span>
                ) : current ? (
                  /* ⚠️ THIS USED TO BE A DEAD LABEL, AND ON THE TOP PLAN THAT
                     MEANT NO CONTROL AT ALL. A Pro subscriber saw "Your plan"
                     here and "Below your plan" on Premium — two pieces of text
                     and nothing to press. They could not move down and they
                     could not stop, from the one page that is about what they
                     pay us. Cancelling lives in Stripe's portal, so this is the
                     door to it rather than a screen of our own. */
                  <div className={styles.currentActions}>
                    <span className={styles.current}>Your plan</span>
                    <button
                      type="button"
                      className={styles.linkAction}
                      disabled={portal.opening}
                      onClick={() => void portal.open({ intent: "cancel" })}
                    >
                      {portal.pending === "cancel" ? "Opening…" : "Cancel or manage billing"}
                    </button>
                  </div>
                ) : subscribed && RANK[plan.id] < currentRank ? (
                  /* Below what they already pay for — and now an offer rather
                     than a statement.

                     The old comment here said a downgrade was "a cancellation
                     that runs to the end of their term, not a price swap, so
                     there is nothing to click". That was true of OUR upgrade
                     route, which refuses anything that is not a rise in rank. It
                     was never true of Stripe's, which swaps the price in either
                     direction and states the credit instead of the charge.

                     Same confirm-at-Stripe page as the upgrade below, and it has
                     to be: this is the one that takes money in the other
                     direction, so the customer should see the numbers on the
                     page their bank statement points at. */
                  <Cta
                    disabled={portal.opening || up.busy}
                    onClick={() => void changePlan(plan)}
                  >
                    {portal.pending === plan.id || (up.busy && up.plan?.tier === plan.id)
                      ? "Opening…"
                      : `Switch to ${plan.name}`}
                  </Cta>
                ) : !purchasingAvailable() ? (
                  /* One gate over every branch that BUYS something — subscribe,
                     upgrade, and the sign-in that leads to either.

                     Below the two informational branches on purpose, and the
                     first attempt got this wrong: placed above them, it replaced
                     "Below your plan" with "Coming soon" for a subscriber
                     looking at a cheaper tier. That is a statement about the
                     subscription they already have, not an offer, and switching
                     purchasing off should not change it. Three tests caught it. */
                  <span className={styles.soon}>Coming soon</span>
                ) : status !== "authenticated" ? (
                  <Cta to={`/login?next=${encodeURIComponent("/pricing")}`}>Sign in to subscribe</Cta>
                ) : subscribed ? (
                  /*
                   * Already paying us: this is an UPGRADE, not a purchase.
                   *
                   * Sending an existing subscriber through checkout is what
                   * created the double-billing bug — they came out the other side
                   * with two live subscriptions and paid for both. Both routes
                   * below swap the price on the subscription they already have,
                   * so Stripe credits what they haven't used against the new
                   * plan; neither opens a second one.
                   *
                   * ⚠️ THE CONFIRMATION MOVED TO STRIPE (Ned, 2026-08-30). This
                   * used to call `up.preview`, which showed our own proration
                   * panel and then charged on our own confirm button. The numbers
                   * were right — they come from Stripe's invoice preview — but
                   * the customer was approving a charge against a card they gave
                   * to Stripe, on a page we control, and we kept the only record
                   * of what they agreed to. Stripe's confirm page states the same
                   * difference and holds the record, on the domain their bank
                   * statement already points at.
                   *
                   * useUpgrade is deliberately still here and still tested. It is
                   * the programmatic path and the fallback if the portal's
                   * `subscription_update` configuration is ever unavailable; what
                   * changed is which one the BUTTON uses.
                   */
                  <Cta
                    disabled={portal.opening || up.busy}
                    onClick={() => void changePlan(plan)}
                  >
                    {portal.pending === plan.id || (up.busy && up.plan?.tier === plan.id)
                      ? "Opening…"
                      : `Upgrade to ${plan.name}`}
                  </Cta>
                ) : (
                  <Cta
                    disabled={starting}
                    onClick={() => void start({ kind: "tier", tier: plan.id }, plan.priceId, next)}
                  >
                    {starting ? "Starting…" : `Go ${plan.name}`}
                  </Cta>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/*
        * Upgrades charge the prorated difference IMMEDIATELY to the card already
        * on file, so the amount is stated and confirmed before anything happens.
        * The figure comes from Stripe's upcoming invoice, not from arithmetic
        * over the price table here — those can disagree, and the invoice is the
        * one that charges.
        *
        * ⚠️ A DIALOG, NOT A PANEL, SINCE 2026-08-29. This was a Card rendered here,
        * after the plan grid, which on a full viewport put the confirmation BELOW
        * THE FOLD — so clicking "Upgrade to Pro" looked like it did nothing, with
        * the amount and both buttons correct and off-screen. It was reported as
        * "it isn't redirecting to Stripe", which an upgrade never does: the card
        * is already on file, so the feedback was the entire interaction.
        *
        * Gated on `up.plan` alone, NOT on the amount having arrived. Waiting for
        * the figure would leave the same silent gap, just shorter — the dialog
        * opens on the click and says it is working. Nothing is confirmable until
        * Stripe answers; UpgradeDialog disables the button, not the dialog.
        */}
      {up.plan && (
        <UpgradeDialog
          planName={PLANS.find((p) => p.id === up.plan?.tier)?.name ?? "the new plan"}
          amountCents={up.amountCents}
          currency={up.currency}
          lines={up.lines}
          taxCents={up.taxCents}
          scheduledFor={
            up.scheduled ? formatResetDate(up.effectiveAtIso ?? undefined) : undefined
          }
          busy={up.busy}
          onConfirm={() => void up.confirm()}
          onDismiss={up.cancel}
        />
      )}

      {/* The credit pack.

          Rendered only when it can actually be bought — `topUpAvailable()` is
          false while `TOP_UP.priceId` is null, which is how the whole feature
          ships dark. The alternative, a card saying "coming soon", is what the
          fine print below has effectively been doing for a week: promising
          top-ups on a page that cannot sell them.

          Priced ABOVE every marginal upgrade step on purpose (see plans.ts), so
          this is the expensive way to buy volume and the ladder still points up.
          Deliberately not dressed as a plan: it sits after the plans, in one
          line, because someone who should be upgrading should upgrade. */}
      {topUpAvailable() && (
        <Card className={styles.topUp}>
          <div>
            <span className={styles.topUpLabel}>Out before your reset?</span>
            <p className={styles.topUpBody}>
              {TOP_UP.credits} extra credits for ${TOP_UP.priceAud.toFixed(2)}. They don&rsquo;t
              expire with your monthly allowance — top-ups roll over.
            </p>
          </div>
          <Cta
            variant="secondary"
            disabled={starting}
            onClick={() => void start({ kind: "topup" }, TOP_UP.priceId!, next)}
          >
            {starting ? "Starting…" : "Buy credits"}
          </Cta>
        </Card>
      )}

      {error && <p className={styles.error}>{error}</p>}
      {up.error && <p className={styles.error}>{up.error}</p>}

      <p className={styles.note}>
        Billed monthly. Cancel any time and you keep access to the end of the cycle. Credits
        reset monthly and don&rsquo;t roll over. Top-ups do. Prices in AUD, GST
        included. 18+.
      </p>
    </Section>
  );
}

const COUNT_WORDS = ["no", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"];

/** "six", not "6", in a sentence; digits past ten. */
function countWord(n: number): string {
  return COUNT_WORDS[n] ?? String(n);
}

/** "AFL Fantasy and AFL Sport"; "A, B and C". */
function listNames(names: string[]): string {
  return names.length <= 1
    ? (names[0] ?? "")
    : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}
